import { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useApolloClient, DocumentNode, ApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { CompleteExpressQueryKiosk } from './__queries__';
import COMPLETE_QUERY from './completeQuery';
import { useI18n } from '~Hooks';

const ITEM_FRAGMENT = gql`
  fragment itemInBongKiosk on Item {
    id
    ageRestricted
    order
    defaultPrice
    titleLang {
      id
      se
      en
      es
      no
    }
    vatRate
    descriptionLang {
      id
      se
      en
      es
      no
    }
    image {
      id
    }

    upsalesSortOrder
    upsales {
      id
      hideInEPOS
      enableMultiSelect
      name {
        id
        se
        en
        es
        no
      }
      items {
        id
      }
    }

    categories {
      id
    }

    modificationGroups
    allergyTags
  }
`;

const VARIATION_FRAGMENT = gql`
  fragment variationInBongKiosk on Variation {
    id
    name
    nameLang {
      id
      se
      en
      es
      no
    }
    asItem {
      id
    }
    price
    image {
      id
    }
  }
`;

const FRAGMENTS = {
  VariationGroup: gql`
    fragment variationGroupInBongKiosk on VariationGroup {
      id
      required
      type
      title {
        id
        se
        en
        es
        no
      }
      variations {
        allergen
        id
      }
      preselectedVariations {
        id
      }
    }
  `,

  Offer: gql`
    fragment offerInBongKiosk on Offer {
      id
      nameLang {
        id
        se
        en
        es
        no
      }
      image {
        id
      }
      type
      discountType
      discountRate

      items {
        id
      }

      stages {
        id
        discountRate
        discountType
        title {
          id
          se
          en
          es
          no
        }
        items {
          id
        }
      }
    }
  `,
};

const IMAGES_QUERY = gql`
  query getImagesForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        images {
          id
          file {
            id
            url
          }
        }
      }
    }
  }
`;

const VARIATIONS_QUERY = gql`
  query ExpressVariations($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        variations {
          ...variationInBongKiosk
        }
      }
    }
  }

  ${VARIATION_FRAGMENT}
`;

const VARIATION_GROUPS_QUERY = gql`
  query ExpressVariationGroups($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        variationGroups {
          ...variationGroupInBongKiosk
        }
      }
    }
  }

  ${FRAGMENTS.VariationGroup}
`;

const ITEMS_QUERY = gql`
  query ExpressItems($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        items {
          ...itemInBongKiosk
        }
      }
    }
  }

  ${ITEM_FRAGMENT}
`;

const VENUE_QUERY = gql`
  query ExpressVenue($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      availabilities {
        id
        dayOfWeek
        timeRange {
          id
          start
          end
        }
      }
      image {
        id
      }
      company {
        id
        defaultOrderStatus
        offers {
          id
          price
          type
          discountType
          discountRate
          overrideItemPrices
          nameLang {
            id
            se
            en
            es
            no
          }
          descriptionLang {
            id
            se
            en
            es
            no
          }
          image {
            id
          }
          items {
            id
            titleLang {
              id
              se
              en
              es
              no
            }
          }
          stages {
            id
            discountRate
            discountType
            title {
              id
              se
              en
              es
              no
            }
            items {
              id
            }
          }
        }
      }
    }
  }
`;

const EXPRESS_PAGES_QUERY = gql`
  query ExpressPages($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        expressPages {
          id
          order
          deleted
          description {
            id
            en
            se
          }
          icon {
            id
          }
          image {
            id
          }
          title {
            id
            en
            se
            es
            no
          }
          description {
            id
            en
            se
          }
          categories {
            id
          }
        }
      }
    }
  }
`;

const VENUE_CATEGORIES_QUERY = gql`
  query VenueCategories($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        categories {
          id
          order
          color
          deleted
          hideInCustomerOrderingFlows
          ukVatRate
          name

          nameLang {
            id
            se
            en
            es
            no
          }
          descriptionLang {
            id
            se
            en
            es
            no
          }

          items(filter: { deleted: false }) {
            id
          }

          image {
            id
          }
        }
      }
    }
  }
`;

function queryAndRetry(
  client: ApolloClient<any>,
  query: DocumentNode,
  variables: object | null,
  iteration = 0,
) {
  function onFail() {
    return new Promise((resolve, reject) => {
      if (iteration >= 5) {
        resolve(null);
        return;
      }

      setTimeout(() => {
        queryAndRetry(client, query, variables, iteration + 1)
          .then(resolve)
          .catch(reject);
      }, iteration * 250);
    });
  }

  return client
    .query({
      query,
      variables: variables || {},
    })
    .then((response) => {
      if (!response.data) {
        console.warn(response);
        return onFail();
      }

      return response;
    })
    .catch(onFail);
}

const useExpressQuery = (venueId: string) => {
  const client = useApolloClient();
  const { i18n } = useI18n();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState(`${i18n('App.Loading')}...`);
  const [data, setData] = useState<CompleteExpressQueryKiosk | null>(null);

  const randomOffset = () => Math.round(Math.random() * 9);

  async function runQuery() {
    try {
      setProgress(5 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingVariations')}...`);

      await queryAndRetry(client, VARIATIONS_QUERY, {
        venueId,
      });

      setProgress(25 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingVariationGroups')}...`);

      await queryAndRetry(client, VARIATION_GROUPS_QUERY, {
        venueId,
      });

      setProgress(35 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingVenue')}...`);

      await queryAndRetry(client, VENUE_QUERY, {
        venueId,
      });

      setProgress(45 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingExpressPages')}...`);

      await queryAndRetry(client, EXPRESS_PAGES_QUERY, {
        venueId,
      });

      setProgress(55 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingImages')}...`);

      await queryAndRetry(client, IMAGES_QUERY, {
        venueId,
      });

      setProgress(64 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingItems')}...`);

      await queryAndRetry(client, ITEMS_QUERY, {
        venueId,
      });

      setProgress(80 + randomOffset());
      setProgressText(`${i18n('SSK.LoadingCategories')}...`);

      await queryAndRetry(client, VENUE_CATEGORIES_QUERY, {
        venueId,
      });

      /**
       * This is the complete query. It fetches the whole resource
       * object from the cache. Finishes immediately.
       */
      const response = await client.readFragment({
        fragment: COMPLETE_QUERY,
        id: venueId,
        fragmentName: 'CompleteExpressQueryKiosk',
      });

      // We're finished!
      setProgress(100);
      setLoading(false);
      setData(response);
    } catch (e) {
      // @ts-ignore
      setProgressText(progressText + ' ' + e.message);
    }
  }

  useEffect(() => {
    runQuery();
  }, []);

  return { loading, progress, progressText, data };
};

export default useExpressQuery;
